import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, cfmt, pfmt } from '../lib/api';
import SEOHead from '../components/common/SEOHead';
import NeighbourhoodCard from '../components/common/NeighbourhoodCard';
import MapView from '../components/common/MapView';
import NewsletterForm from '../components/common/NewsletterForm';
import { ArrowUpRight } from 'lucide-react';

const VALID_SLUGS = new Set([
    'durham', 'hamilton', 'burlington', 'oakville', 'milton',
    'halton-hills', 'brantford', 'brant-county', 'niagara',
]);

export default function RegionPage() {
    // Route mounted at explicit paths ('/durham', '/hamilton', ...) — read slug from URL.
    const location = useLocation();
    const slug = location.pathname.replace(/^\//, '').split('/')[0];

    const { data, isLoading, error } = useQuery({
        queryKey: ['focus-area', slug],
        queryFn: async () => (await api.get(`/focus-areas/${slug}`)).data,
        enabled: VALID_SLUGS.has(slug),
        retry: false,
    });

    if (!VALID_SLUGS.has(slug)) return <Navigate to="/" replace />;
    if (isLoading) return <div className="max-w-[1400px] mx-auto p-16 text-neutral-500">Loading…</div>;
    if (error || !data) return <div className="max-w-[1400px] mx-auto p-16 text-neutral-500">This region isn&apos;t catalogued yet.</div>;

    const seoDescription =
        `${data.blurb} We cover ${data.count} neighbourhoods in ${data.name} with schools, safety, walkability, ` +
        `affordability and appreciation data. Median price ${cfmt(data.avg_price)}. Average 5-year forecast +${pfmt(data.avg_forecast_5yr)}.`;

    return (
        <div>
            <SEOHead
                title={`${data.name} neighbourhoods — data, schools, prices`}
                description={seoDescription}
                path={`/${slug}`}
                type="article"
            />

            {/* HERO */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <div className="overline text-[#2F6B47]">Focus market · Southern Ontario</div>
                        <h1 className="font-editorial text-[52px] lg:text-[80px] leading-[1] tracking-editorial mt-4">
                            {data.name}
                        </h1>
                        <p className="mt-6 text-[19px] text-neutral-700 max-w-xl font-editorial italic">{data.blurb}</p>
                        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-neutral-200 max-w-2xl">
                            {[
                                { l: 'Neighbourhoods', v: data.count },
                                { l: 'Median price', v: cfmt(data.avg_price) },
                                { l: '1Y change', v: `${data.avg_1yr_change >= 0 ? '+' : ''}${pfmt(data.avg_1yr_change)}` },
                                { l: '5Y forecast', v: `+${pfmt(data.avg_forecast_5yr)}`, tone: 'brand' },
                            ].map((s) => (
                                <div key={s.l} className="border-r border-b border-neutral-200 p-4">
                                    <div className="overline">{s.l}</div>
                                    <div className={`font-mono-data text-[20px] tabular mt-2 ${s.tone === 'brand' ? 'text-[#2F6B47]' : ''}`}>{s.v}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8 text-[13px] font-mono-data text-neutral-500">
                            Range: {cfmt(data.min_price)} — {cfmt(data.max_price)}
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <MapView items={data.neighbourhoods} height={420} showCatchment />
                    </div>
                </div>
            </section>

            {/* NEIGHBOURHOODS GRID */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <div className="overline">All neighbourhoods · Ranked by 5Y forecast</div>
                            <h2 className="font-editorial text-[34px] tracking-editorial mt-3">The complete {data.name} catalogue</h2>
                        </div>
                        <Link to="/neighbourhoods" className="hidden md:inline-flex items-center gap-2 text-[13.5px] link-editorial">
                            All areas <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {data.neighbourhoods.map((n) => <NeighbourhoodCard key={n.slug} n={n} />)}
                    </div>
                </div>
            </section>

            {/* Editorial closer + newsletter */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-6">
                        <div className="overline">Weekly briefing · {data.name}</div>
                        <h3 className="font-editorial text-[36px] lg:text-[52px] tracking-editorial mt-4 leading-[1.05]">
                            One {data.name} number worth knowing. Every Sunday.
                        </h3>
                    </div>
                    <div className="lg:col-span-6 lg:pl-16 flex flex-col justify-center">
                        <p className="text-[15px] text-neutral-600 max-w-md">
                            When a school catchment shifts, a GO station opens, or a neighbourhood breaks trend — we send one paragraph.
                        </p>
                        <div className="mt-6 max-w-md">
                            <NewsletterForm source={`region:${slug}`} tags={[`region:${slug}`]} />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
